// frontend/src/components/products/ProductsCatalogView.js
import React, { useState, useEffect } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard } from '../common/CommonComponents';

function ProductsCatalogView({ showAlert, user, onAddToCart, cartItems = [] }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQ, setSearchQ] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [quantities, setQuantities] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);

  const showAddToCart = user.role === "ambassador" && typeof onAddToCart === "function";

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = () => {
    setLoading(true);
    api.getFinishedGoods({ activeOnly: "true" })
      .then(products => {
        const normalized = products.map(p => ({...p,images:p.images&&p.images.length?p.images:(p.imageUrl?[p.imageUrl]:[])}));
        const located=user.role==="ambassador"
          ? normalized.flatMap(p=>(p.locations||[]).filter(loc=>Number(loc.quantity)>0).map(loc=>({...p,quantity:Number(loc.quantity),fulfillmentShopId:loc.shopId||null,fulfillmentShopName:loc.shopName||"Central",cartKey:`${p.productId}::${loc.shopId||"__CENTRAL__"}`})))
          : normalized;
        setProducts(located);
      })
      .catch(() => showAlert("Failed to load products", "error"))
      .finally(() => setLoading(false));
  };

  const categories = ["all", ...new Set(products.map(p => p.category).filter(Boolean))];

  const filteredProducts = products.filter(p => {
    if (selectedCategory !== "all" && p.category !== selectedCategory) return false;
    if (searchQ && !p.name.toLowerCase().includes(searchQ.toLowerCase()) && 
        !(p.category || "").toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  const handleQuantityChange = (productId, value) => {
    const qty = Math.max(1, parseInt(value) || 1);
    setQuantities(prev => ({ ...prev, [productId]: qty }));
  };

  const handleAddToCart = (product) => {
    const key=product.cartKey||product.productId;
    const qty = quantities[key] || 1;
    if (qty > product.quantity) {
      showAlert(`Only ${product.quantity} units in stock`, "error");
      return;
    }
    onAddToCart(product, qty);
    setQuantities(prev => ({ ...prev, [key]: 1 }));
    showAlert(`Added ${qty} x ${product.name} to cart`, "success");
  };

  const handleShareToWhatsAppStatus = async (product) => {
    const imageUrl = product.images?.[0] || product.imageUrl;
    if (!imageUrl) return;

    const caption = [
      product.name,
      product.category ? `Category: ${product.category}` : "",
      `Price: ${fmt(product.sellingPrice)}`,
      Number(product.quantity) > 0 ? `In stock: ${product.quantity}` : "Out of stock",
      product.description || "",
    ].filter(Boolean).join("\n");

    try {
      const response = await fetch(imageUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Could not load product image");
      const blob = await response.blob();
      const ext = (blob.type.split("/")[1] || "jpg").replace("jpeg", "jpg");
      const safeName = String(product.name || "product").replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase() || "product";
      const file = new File([blob], `${safeName}.${ext}`, { type: blob.type || "image/jpeg" });

      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share({
          files: [file],
          text: caption,
          title: product.name,
        });
        return;
      }

      if (navigator.share) {
        await navigator.share({ text: caption, title: product.name });
        showAlert("Your browser shared the product text, but not the image. For WhatsApp Status with the product image, use this button on a supported mobile browser.", "info");
        return;
      }

      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(caption);
      window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank", "noopener,noreferrer");
      showAlert("WhatsApp opened with the product caption. This browser cannot attach the image directly; use the button on a supported phone to share the image to My Status.", "info");
    } catch (err) {
      if (err?.name === "AbortError") return;
      console.error("WhatsApp product share failed:", err);
      showAlert("Could not share this product image. Try again from a supported mobile browser.", "error");
    }
  };

  const openGallery = (product, index = 0) => {
    setSelectedProduct(product);
    setGalleryIndex(index);
  };

  const closeGallery = () => {
    setSelectedProduct(null);
    setGalleryIndex(0);
  };

  const nextImage = () => {
    if (selectedProduct && selectedProduct.images && selectedProduct.images.length > 0) {
      setGalleryIndex((prev) => (prev + 1) % selectedProduct.images.length);
    }
  };

  const prevImage = () => {
    if (selectedProduct && selectedProduct.images && selectedProduct.images.length > 0) {
      setGalleryIndex((prev) => (prev - 1 + selectedProduct.images.length) % selectedProduct.images.length);
    }
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        nextImage();
      } else {
        prevImage();
      }
    }
    setTouchStart(0);
  };

  if (loading) return <div className="spinner">Loading products...</div>;

  return (
    <div>
      {/* Fullscreen Gallery Modal */}
      {selectedProduct && selectedProduct.images && selectedProduct.images.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,.95)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={closeGallery}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Main Image */}
          <img
            src={selectedProduct.images[galleryIndex]}
            alt={`${selectedProduct.name} - ${galleryIndex + 1}`}
            style={{
              maxWidth: "90vw",
              maxHeight: "70vh",
              objectFit: "contain",
              borderRadius: 8,
            }}
          />

          {/* Image Counter */}
          <div
            style={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(0,0,0,.6)",
              color: "#fff",
              padding: "4px 12px",
              borderRadius: 20,
              fontSize: 12,
            }}
          >
            {galleryIndex + 1} / {selectedProduct.images.length}
          </div>

          {/* Thumbnails */}
          {selectedProduct.images.length > 1 && (
            <div
              style={{
                position: "absolute",
                bottom: 60,
                left: 0,
                right: 0,
                display: "flex",
                justifyContent: "center",
                gap: 8,
                flexWrap: "wrap",
                padding: "8px 16px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {selectedProduct.images.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Thumb ${idx + 1}`}
                  onClick={() => setGalleryIndex(idx)}
                  style={{
                    width: 50,
                    height: 50,
                    objectFit: "cover",
                    borderRadius: 4,
                    border: idx === galleryIndex ? "2px solid #10B981" : "1px solid rgba(255,255,255,.3)",
                    cursor: "pointer",
                  }}
                />
              ))}
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={closeGallery}
            style={{
              position: "absolute",
              top: 20,
              right: 20,
              background: "rgba(0,0,0,.5)",
              color: "#fff",
              border: "none",
              borderRadius: "50%",
              width: 40,
              height: 40,
              fontSize: 20,
              cursor: "pointer",
            }}
          >
            ✕
          </button>

          {/* Navigation Arrows */}
          {selectedProduct.images.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevImage();
                }}
                style={{
                  position: "absolute",
                  left: 20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,.5)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  fontSize: 20,
                  cursor: "pointer",
                }}
              >
                ◀
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextImage();
                }}
                style={{
                  position: "absolute",
                  right: 20,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "rgba(0,0,0,.5)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  fontSize: 20,
                  cursor: "pointer",
                }}
              >
                ▶
              </button>
            </>
          )}
        </div>
      )}

      {/* Search Input */}
      <div style={{ marginBottom: 12 }}>
        <input
          placeholder="🔍 Search products..."
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            border: "1.5px solid var(--border)",
            borderRadius: 8,
            fontSize: 13,
            outline: "none",
            background: "var(--input-bg)",
            color: "var(--text)",
            boxSizing: "border-box",
          }}
        />
      </div>

      {/* Category Filters */}
      {categories.length > 1 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`btn-sm ${selectedCategory === cat ? "btn-primary" : "btn-secondary"}`}
              style={{ fontSize: 11 }}
            >
              {cat === "all" ? "All" : cat}
            </button>
          ))}
        </div>
      )}

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text-3)" }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🛍️</div>
          <p>No products found.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 16 }}>
          {filteredProducts.map(p => {
            const key=p.cartKey||p.productId;
            const inCart = cartItems?.find(i => (i.cartKey||i.productId) === key) || false;
            const currentQty = quantities[key] || 1;
            const hasMultipleImages = p.images && p.images.length > 1;

            return (
              <div
                key={p.cartKey||p.productId}
                style={{
                  border: inCart && showAddToCart ? "2px solid #10B981" : "1px solid var(--border)",
                  borderRadius: 12,
                  overflow: "hidden",
                  background: "var(--surface)",
                  transition: "all 0.2s",
                  position: "relative",
                }}
              >
                {/* Product Image - Click to open gallery */}
                <div
                  style={{ height: 140, background: "var(--surface2)", overflow: "hidden", cursor: "pointer", position: "relative" }}
                  onClick={() => p.images && p.images.length > 0 && openGallery(p, 0)}
                >
                  {p.images && p.images.length > 0 ? (
                    <>
                      <img
                        src={p.images[0]}
                        alt={p.name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.2s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                      />
                      {hasMultipleImages && (
                        <div
                          style={{
                            position: "absolute",
                            bottom: 4,
                            right: 4,
                            background: "rgba(0,0,0,.6)",
                            color: "#fff",
                            padding: "2px 6px",
                            borderRadius: 4,
                            fontSize: 10,
                          }}
                        >
                          📷 {p.images.length}
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: 48 }}>🛍️</div>
                  )}
                </div>

                {inCart && showAddToCart && (
                  <div style={{ position: "absolute", top: 8, right: 8, background: "#10B981", color: "#fff", padding: "2px 8px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
                    In Cart
                  </div>
                )}

                <div style={{ padding: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {p.name}
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-3)", marginBottom: 2 }}>{p.category || "Uncategorized"}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "var(--accent)" }}>{fmt(p.sellingPrice)}</div>
                  <div style={{ fontSize: 10, color: "var(--text-3)", marginBottom: 2 }}>Stock: {p.quantity} units</div>
                  {showAddToCart && <div style={{ fontSize:10,color:"var(--text-3)",marginBottom:8 }}>Fulfilled by: <strong>{p.fulfillmentShopName||"Central"}</strong></div>}

                  {p.images && p.images.length > 0 && (
                    <button
                      className="btn-sm btn-secondary"
                      onClick={() => handleShareToWhatsAppStatus(p)}
                      style={{ width: "100%", fontSize: 11, marginBottom: showAddToCart ? 8 : 0 }}
                      title="On supported phones, choose WhatsApp and then My Status"
                    >
                      📲 Share to WhatsApp Status
                    </button>
                  )}

                  {showAddToCart && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input
                        type="number"
                        min="1"
                        max={p.quantity}
                        value={currentQty}
                        onChange={e => handleQuantityChange(p.cartKey||p.productId, e.target.value)}
                        style={{ width: 60, padding: "4px 6px", border: "1px solid var(--border)", borderRadius: 4, fontSize: 12, textAlign: "center" }}
                      />
                      <button
                        className={`btn-sm ${inCart ? "btn-success" : "btn-primary"}`}
                        onClick={() => handleAddToCart(p)}
                        style={{ flex: 1, fontSize: 11 }}
                        disabled={p.quantity === 0}
                      >
                        {inCart ? "➕ Add More" : "Add to Cart"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ProductsCatalogView;